import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { db, productDb } from '@/shared/firebase/client'

/**
 * Season goals: the target, and how the season is actually going against it.
 *
 * ## Counted, never loaded
 *
 * Nine months of this season and nine of last is eighteen numbers. Read as
 * documents that is 49,623 user records twice over, which is what killed the
 * audience preview at 256 MiB before it was rewritten. `getCountFromServer`
 * answers each month without transferring a single document, so the whole card
 * costs eighteen aggregations.
 *
 * ## Two collections, two projects
 *
 * New users come from the product's `users`; new subscriptions come from the
 * CRM's own `skidaCrmSubscriptions`, which is the RevenueCat mirror and already
 * carries history back through 2024. The product's `billingSubscriptions` holds
 * six documents and is not the source of anything.
 *
 * ## `createdAt` is a number, and a third of accounts do not have one
 *
 * It is epoch milliseconds rather than a Firestore Timestamp — a range query
 * has to compare against numbers or it silently matches nothing, which is how
 * this looked like an empty collection on the first attempt.
 *
 * Roughly 17,000 of 49,623 accounts predate the field. They cannot appear in
 * any month, so a cumulative "so far this season" is a count of the accounts we
 * can date and not of all accounts. That gap is stated on the card: a number
 * that silently excludes a third of the population is worse than no number.
 */

export interface SeasonGoalConfig {
  /** `YYYY-MM` — the month the season opens. */
  seasonStart: string
  /** `YYYY-MM` — the month it closes, inclusive. */
  seasonEnd: string
  usersTarget: number
  /**
   * Installs from the App Store and Google Play across the season.
   *
   * The widest of the targets and the only one measured outside our own systems
   * — see `storeDaysRepository`. It sits above users because it is the top of
   * the funnel: everything else is a share of it.
   */
  downloadsTarget: number
  subsTarget: number
  /** In dollars, because that is the currency RevenueCat reports. */
  grossTarget: number
  /**
   * How the target is spread across the season's months, in percent.
   *
   * A flat twelfth would be wrong for this business: nobody signs up for a ski
   * app in July. The shape is the point, and it is why the target is a line per
   * month rather than a single number divided at the end.
   */
  monthlyPct: number[]
}

const CONFIG_DOC = 'appSeasonGoal'

/** A northern-hemisphere season, weighted to the months people actually ski. */
export const DEFAULT_CONFIG: SeasonGoalConfig = {
  // The design's season and its shape: August through April, weighted to
  // midwinter. Not September–May, which is what this used to guess.
  seasonStart: '2025-08',
  seasonEnd: '2026-04',
  usersTarget: 30000,
  downloadsTarget: 160000,
  subsTarget: 1200,
  grossTarget: 120000,
  monthlyPct: [6, 7, 9, 11, 13, 15, 17, 13, 9],
}

export function monthsOf(cfg: SeasonGoalConfig): string[] {
  const [sy, sm] = cfg.seasonStart.split('-').map(Number)
  const [ey, em] = cfg.seasonEnd.split('-').map(Number)
  const out: string[] = []
  let y = sy!
  let m = sm!
  // Guarded at 24 so a reversed or mistyped range cannot spin forever.
  while (out.length < 24 && (y < ey! || (y === ey! && m <= em!))) {
    out.push(`${y}-${String(m).padStart(2, '0')}`)
    m++
    if (m > 12) {
      m = 1
      y++
    }
  }
  return out
}

/** `2026-01` → the epoch-ms range it covers, [from, to). */
function rangeOf(month: string): [number, number] {
  const [y, m] = month.split('-').map(Number)
  return [Date.UTC(y!, m! - 1, 1), Date.UTC(m! === 12 ? y! + 1 : y!, m! === 12 ? 0 : m!, 1)]
}

const shiftYear = (month: string, by: number) => {
  const [y, m] = month.split('-')
  return `${Number(y) + by}-${m}`
}

async function countUsersIn(month: string): Promise<number> {
  const [from, to] = rangeOf(month)
  const snap = await getCountFromServer(
    query(
      collection(productDb, 'users'),
      where('createdAt', '>=', from),
      where('createdAt', '<', to),
    ),
  )
  return snap.data().count
}

/**
 * New subscriptions in a month.
 *
 * `skidaCrmSubscriptions` in the CRM project, not `billingSubscriptions` in the
 * product — that one holds six documents and is not the source of anything.
 * This one holds 5,210, already backfilled from RevenueCat, and it is what the
 * dashboard's own caveat card points at.
 *
 * It counts **subscriptions, not people**: an older model let one account hold
 * several at once, so 5,210 subscriptions belong to 4,766 subscribers. That is
 * the right number for "how many new subscriptions this month" and the wrong
 * one for "how many people" — `skidaCrmSubscribers` answers that.
 */
async function countSubsIn(month: string): Promise<number> {
  const [from, to] = rangeOf(month)
  const snap = await getCountFromServer(
    query(
      collection(db, 'skidaCrmSubscriptions'),
      where('startsAtMs', '>=', from),
      where('startsAtMs', '<', to),
    ),
  )
  return snap.data().count
}

export interface MonthPoint {
  month: string
  label: string
  actual: number | null
  lastYear: number
  /**
   * Whether last season's figure is a measurement or an absence.
   *
   * Not the same question as "is it zero". 17,465 accounts predate `createdAt`
   * being written, and they are the older ones — so for users, every month of
   * last season counts zero regardless of how many people signed up. Drawing
   * that as a zero bar does not read as "no data", it reads as "last year
   * nobody joined".
   *
   * The design's answer, and it is better than the note this replaces: a
   * hatched stub where the bar would be. Subscriptions have both seasons
   * properly and are always known.
   */
  lastYearKnown: boolean
  target: number
}

export interface SeasonSeries {
  months: MonthPoint[]
  cumActual: number
  cumTarget: number
  cumLastYear: number
  /** Index of the month we are in; later months have no actual yet. */
  elapsed: number
}

const LABEL = (month: string) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString('nb-NO', { month: 'short', timeZone: 'UTC' })

/**
 * Gross revenue booked in a month, in dollars.
 *
 * Summed rather than counted, so unlike the other two this reads documents —
 * there is no aggregation for "add up a field" across a range. 5,210 documents
 * is small enough that this is fine and would not be at ten times the size.
 *
 * **Sandbox subscriptions are excluded.** 275 of the 5,210 are App Store
 * sandbox records with real-looking prices on them. Counting those would
 * overstate revenue by whatever a tester happened to buy that week.
 */
async function grossIn(month: string): Promise<number> {
  const [from, to] = rangeOf(month)
  const snap = await getDocs(
    query(
      collection(db, 'skidaCrmSubscriptions'),
      where('startsAtMs', '>=', from),
      where('startsAtMs', '<', to),
    ),
  )
  let sum = 0
  for (const d of snap.docs) {
    const v = d.data()
    if (v.sandbox === true) continue
    if (typeof v.grossUsd === 'number') sum += v.grossUsd
  }
  return Math.round(sum)
}

export async function loadSeason(
  cfg: SeasonGoalConfig,
  metric: 'users' | 'subs' | 'gross',
): Promise<SeasonSeries> {
  const months = monthsOf(cfg)
  const now = new Date()
  const nowKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
  const elapsed = Math.max(0, months.findIndex((m) => m >= nowKey))
  const lastIndex = elapsed === -1 ? months.length - 1 : elapsed

  const count = metric === 'users' ? countUsersIn : metric === 'subs' ? countSubsIn : grossIn
  const total =
    metric === 'users' ? cfg.usersTarget : metric === 'subs' ? cfg.subsTarget : cfg.grossTarget

  // Users have an unrecorded past; subscriptions and revenue do not. Asked
  // once for the whole series rather than per month — the answer cannot differ
  // between months, and it is an aggregation over 49,623 documents.
  const undated = metric === 'users' ? await countUndatedUsers() : 0

  const [actuals, lastYears] = await Promise.all([
    Promise.all(months.map((m, i) => (i <= lastIndex ? count(m) : Promise.resolve(null)))),
    Promise.all(months.map((m) => count(shiftYear(m, -1)))),
  ])

  let cumActual = 0
  let cumTarget = 0
  let cumLastYear = 0

  const points: MonthPoint[] = months.map((month, i) => {
    const target = Math.round(total * ((cfg.monthlyPct[i] ?? 0) / 100))
    const actual = actuals[i]
    const lastYear = Math.max(0, lastYears[i] ?? 0)
    if (i <= lastIndex) {
      cumActual += Math.max(0, actual ?? 0)
      cumTarget += target
      cumLastYear += lastYear
    }
    // A zero from a metric with undated accounts behind it is an absence, not
    // a measurement. A non-zero is real either way.
    const lastYearKnown = lastYear > 0 || undated === 0
    return { month, label: LABEL(month), actual, lastYear, lastYearKnown, target }
  })

  return { months: points, cumActual, cumTarget, cumLastYear, elapsed: lastIndex }
}

export function subscribeToConfig(
  onChange: (cfg: SeasonGoalConfig) => void,
): () => void {
  return onSnapshot(doc(db, 'skidaCrmSettings', CONFIG_DOC), (snap) => {
    const v = snap.data() as Partial<SeasonGoalConfig> | undefined
    onChange({
      seasonStart: v?.seasonStart ?? DEFAULT_CONFIG.seasonStart,
      seasonEnd: v?.seasonEnd ?? DEFAULT_CONFIG.seasonEnd,
      usersTarget: v?.usersTarget ?? DEFAULT_CONFIG.usersTarget,
      downloadsTarget: v?.downloadsTarget ?? DEFAULT_CONFIG.downloadsTarget,
      subsTarget: v?.subsTarget ?? DEFAULT_CONFIG.subsTarget,
      grossTarget: v?.grossTarget ?? DEFAULT_CONFIG.grossTarget,
      monthlyPct: v?.monthlyPct?.length ? v.monthlyPct : DEFAULT_CONFIG.monthlyPct,
    })
  })
}

export async function saveConfig(cfg: SeasonGoalConfig): Promise<void> {
  await setDoc(doc(db, 'skidaCrmSettings', CONFIG_DOC), cfg, { merge: true })
}

/** How many accounts carry no creation date, so the card can say so. */
export async function countUndatedUsers(): Promise<number> {
  const [all, dated] = await Promise.all([
    getCountFromServer(collection(productDb, 'users')),
    getCountFromServer(query(collection(productDb, 'users'), where('createdAt', '>=', 0))),
  ])
  return all.data().count - dated.data().count
}

/** Read once, for callers that do not want a subscription. */
export async function readConfig(): Promise<SeasonGoalConfig> {
  const snap = await getDoc(doc(db, 'skidaCrmSettings', CONFIG_DOC))
  const v = snap.data() as Partial<SeasonGoalConfig> | undefined
  return { ...DEFAULT_CONFIG, ...(v ?? {}) }
}

/**
 * Gross revenue booked inside an arbitrary range, in dollars.
 *
 * The dashboard's Gross USD card. Same exclusion as the season figure: App
 * Store sandbox subscriptions carry real-looking prices and are not revenue.
 *
 * `from === null` means all time, which is 5,210 documents — fine at this size,
 * and the reason the card is a sum rather than an aggregation is that Firestore
 * has no server-side sum over a range.
 */
export async function grossInRange(from: number | null, to: number): Promise<number> {
  const base = collection(db, 'skidaCrmSubscriptions')
  const q =
    from === null
      ? query(base, where('startsAtMs', '<', to))
      : query(base, where('startsAtMs', '>=', from), where('startsAtMs', '<', to))
  const snap = await getDocs(q)
  let sum = 0
  for (const d of snap.docs) {
    const v = d.data()
    if (v.sandbox === true) continue
    if (typeof v.grossUsd === 'number') sum += v.grossUsd
  }
  return Math.round(sum)
}
